import { Center, Flex, Text } from '@chakra-ui/react';
import { decode } from 'js-base64';
import { LayersThree02 } from '@kula-ai/icons-core';
import { KButton } from '@/ui-library/components';
import { useMarkAsReadMutation } from '@/store/query/notification';
import { NotificationCard } from '../../components/Card';
import { NotificationTime } from '../../components/Time';
import {
  RequisitionDetailsSection,
  RequisitionDetailsList,
} from '../requisition-notifications/components';

interface Counts {
  granted?: number;
  pending_response?: number;
  about_to_expire?: number;
  expired?: number;
  refused?: number;
  not_requested?: number;
}

interface Notification {
  id?: string | number;
  custom_attributes?: string;
  [key: string]: any;
}

interface Props {
  notification?: Notification;
  onClose?: () => void;
}

export const CandidateConsentSummaryNotification = ({
  notification = {},
  onClose = () => {},
}: Props) => {
  const [markAsRead] = useMarkAsReadMutation();
  let data: { counts?: Counts; date?: string; expired_declined_url?: string } =
    {};
  try {
    data = JSON.parse(decode(notification.custom_attributes || ''));
  } catch (error) {
    console.error('Error decoding consent summary notification:', error);
  }

  const { counts = {}, date, expired_declined_url: expiredDeclinedUrl } = data;

  // labels + order mirror the summary email/Slack (RTC candidate_consent_summary)
  const detailsData = [
    { label: 'Granted', value: counts.granted },
    { label: 'Refused', value: counts.refused },
    { label: 'About to expire', value: counts.about_to_expire },
    { label: 'Expired', value: counts.expired },
    { label: 'Not requested yet', value: counts.not_requested },
    { label: 'Pending response', value: counts.pending_response },
  ]
    // keep zero counts (drop only missing keys), and stringify so the details
    // list renders "0" instead of coercing a falsy 0 to its "-" placeholder
    .filter((item) => item.value !== undefined && item.value !== null)
    .map((item) => ({ label: item.label, value: String(item.value) }));

  const viewExpiredDeclined = () => {
    markAsRead({ id: notification?.id });
    window.open(expiredDeclinedUrl, '_self');
    onClose();
  };

  return (
    <NotificationCard notification={notification}>
      <Flex alignItems="baseline" gap="6px">
        <Center minW="34px" h="34px" borderRadius="sm" bg="primary-100">
          <LayersThree02
            size="1em"
            color="var(--kula-colors-primary-500)"
            className="kula-icon"
          />
        </Center>
        <Flex flexDir="column" ml="12px" gap="8px" alignSelf="start" flex="1">
          <Text size="sm-semi-bold">
            Candidate consent summary{date ? ` - ${date}` : ''}
          </Text>

          <RequisitionDetailsSection title="Consent summary">
            <RequisitionDetailsList data={detailsData} />
          </RequisitionDetailsSection>

          <NotificationTime notification={notification} />
          {expiredDeclinedUrl && (
            <KButton
              w="fit-content"
              variant="primary"
              borderRadius="8px"
              maxH="34px"
              onClick={viewExpiredDeclined}
              data-test-id="view-expired-declined-button"
            >
              View all expired or declined candidates
            </KButton>
          )}
        </Flex>
      </Flex>
    </NotificationCard>
  );
};
